import * as React from "react";
import { Alert, Divider } from "antd";
import { useDefaultProbes } from "./useDefaultProbes";
import { useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpSwitch } from "../../../../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../CpArrayItemNameValuePair";
import { v4 as uuidv4 } from "uuid";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";
import { CpLabelDirtyIndicator } from "../../../../gvc/testNewForm/CpLabelDirtyIndicator";
import { Only } from "../../only";

interface Props {
  property: "liveness" | "readiness";
  index: number;
}
export const WorkloadContainerProbe: React.FC<Props> = React.memo(({ property, index }) => {
  const label = property === "liveness" ? "Liveness" : "Readiness";

  const type = useCpFormValue(`type`);
  const alreadySet: boolean = useCpFormValue(`containers.${index}.${property}Probe.alreadySet`);
  const override: boolean = useCpFormValue(`containers.${index}.${property}Probe.override`);
  // TODO type here
  const method: string = useCpFormValue(`containers.${index}.${property}Probe.type`);
  const servesTraffic = useCpFormValue(`containers.${index}.servesTraffic`);

  const defaults = useDefaultProbes(index);
  const defaultProbe = defaults[property];

  return (
    <>
      <Divider orientation={`left`} orientationMargin={0}>
        {label} Probe
        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
          <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe`} />
        </Only>
      </Divider>
      {alreadySet ? (
        <CpFormItem>
          <CpLabelTooltip>
            <CpLabel name={`containers.${index}.${property}Probe.alreadySet`}>
              {`Enable ${label} Probe`}
              <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.alreadySet`} />
              </Only>
            </CpLabel>
            <CpSwitch name={`containers.${index}.${property}Probe.alreadySet`} />
            <CpTooltip
              tooltipProps={{
                title: (
                  <>
                    {property === "readiness" ? (
                      <div>A Probe to check if the workload is ready to receive additional traffic.</div>
                    ) : (
                      <div>A Probe to check if the workload is healthy or should be restarted.</div>
                    )}
                  </>
                ),
              }}
            />
          </CpLabelTooltip>
        </CpFormItem>
      ) : (
        <CpFormItem>
          <CpLabelTooltip>
            <CpLabel name={`containers.${index}.${property}Probe.override`}>
              {defaultProbe ? `Override ${label} Probe` : `Set ${label} Probe`}
              <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.override`} />
              </Only>
            </CpLabel>
            <CpSwitch name={`containers.${index}.${property}Probe.override`} />
            <CpTooltip
              tooltipProps={{
                title: (
                  <>
                    {property === "readiness" ? (
                      <div>A Probe to check if the workload is ready to receive additional traffic.</div>
                    ) : (
                      <div>A Probe to check if the workload is healthy or should be restarted.</div>
                    )}
                  </>
                ),
              }}
            />
          </CpLabelTooltip>
        </CpFormItem>
      )}
      {alreadySet || override ? (
        <>
          <CpFormItem>
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <CpLabel name={`containers.${index}.${property}Probe.type`}>
                  Method
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.type`} />
                  </Only>
                </CpLabel>
                <CpSelect
                  name={`containers.${index}.${property}Probe.type`}
                  options={[
                    { label: "HTTP", value: "http" },
                    { label: "Command", value: "exec" },
                    { label: "TCP", value: "tcp" },
                    { label: "GRPC", value: "grpc" },
                  ]}
                  style={{ width: 200 }}
                />
              </div>
              {method === "http" ? (
                <>
                  <div className="flex flex-col">
                    <CpLabel name={`containers.${index}.${property}Probe.httpGet.scheme`}>
                      Scheme
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.httpGet.scheme`} />
                      </Only>
                    </CpLabel>
                    <CpSelect
                      name={`containers.${index}.${property}Probe.httpGet.scheme`}
                      options={[
                        {
                          label: "HTTP",
                          value: "HTTP",
                        },
                        {
                          label: "HTTPS",
                          value: "HTTPS",
                        },
                      ]}
                      style={{ width: 120 }}
                    />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`containers.${index}.${property}Probe.httpGet.path`}>
                      Path
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.httpGet.path`} />
                      </Only>
                    </CpLabel>
                    <CpInput
                      name={`containers.${index}.${property}Probe.httpGet.path`}
                      placeholder="/"
                      style={{ width: 300 }}
                    />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`containers.${index}.${property}Probe.httpGet.port`}>
                      Port
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.httpGet.port`} />
                      </Only>
                    </CpLabel>
                    <CpInputNumber name={`containers.${index}.${property}Probe.httpGet.port`} style={{ width: 120 }} />
                  </div>
                </>
              ) : null}
              {method === "exec" ? (
                <div className="flex flex-col">
                  <CpLabel name={`containers.${index}.${property}Probe.exec.command`}>
                    Command
                    <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                      <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.exec.command`} />
                    </Only>
                  </CpLabel>
                  <CpInput name={`containers.${index}.${property}Probe.exec.command`} style={{ width: 400 }} />
                </div>
              ) : // TODO args
              null}
              {method === "tcp" ? (
                <div className="flex flex-col">
                  <CpLabel name={`containers.${index}.${property}Probe.tcpSocket.port`}>
                    Port
                    <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                      <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.tcpSocket.port`} />
                    </Only>
                  </CpLabel>
                  <CpInputNumber name={`containers.${index}.${property}Probe.tcpSocket.port`} style={{ width: 120 }} />
                </div>
              ) : null}
              {method === "grpc" ? (
                <div className="flex flex-col">
                  <CpLabel name={`containers.${index}.${property}Probe.grpc.port`}>
                    Port
                    <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                      <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.grpc.port`} />
                    </Only>
                  </CpLabel>
                  <CpInputNumber name={`containers.${index}.${property}Probe.grpc.port`} style={{ width: 120 }} />
                </div>
              ) : null}
            </div>
            <CpErrorWarningArray
              names={[
                `containers.${index}.${property}Probe.exec.command`,
                `containers.${index}.${property}Probe.httpGet.port`,
                `containers.${index}.${property}Probe.tcpSocket.port`,
                `containers.${index}.${property}Probe.grpc.port`,
              ]}
            />
          </CpFormItem>
          {method === "exec" ? (
            <div style={{ maxWidth: 607 }}>
              <CpArrayString
                label="Args"
                name={`containers.${index}.${property}Probe.exec.args`}
                dirtyIndicator={{
                  isEnabled: true,
                  only: {
                    name: "meta.isCreate",
                    checkFn: (v) => v === false,
                  },
                }}
              />
            </div>
          ) : null}
          {method === "http" ? (
            <div style={{ maxWidth: 1200 }}>
              <CpArrayObject
                labels={["Name", "Value"]}
                factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                label="Headers"
                name={`containers.${index}.${property}Probe.httpGet.httpHeaders`}
                dirtyIndicator={{
                  isEnabled: true,
                  only: {
                    name: "meta.isCreate",
                    checkFn: (v) => v === false,
                  },
                }}
              >
                <CpArrayItemNameValuePair />
              </CpArrayObject>
            </div>
          ) : null}
          <CpFormItem>
            <div className="flex items-start gap-2">
              <div className="flex flex-col">
                <CpLabel name={`containers.${index}.${property}Probe.initialDelaySeconds`}>
                  Initial Delay Seconds
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.initialDelaySeconds`} />
                  </Only>
                </CpLabel>
                <CpInputNumber
                  name={`containers.${index}.${property}Probe.initialDelaySeconds`}
                  style={{ width: 200 }}
                />
              </div>
              <div className="flex flex-col">
                <CpLabel name={`containers.${index}.${property}Probe.periodSeconds`}>
                  Period Seconds
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.periodSeconds`} />
                  </Only>
                </CpLabel>
                <CpInputNumber name={`containers.${index}.${property}Probe.periodSeconds`} style={{ width: 200 }} />
              </div>
              <div className="flex flex-col">
                <CpLabel name={`containers.${index}.${property}Probe.timeoutSeconds`}>
                  Timeout Seconds
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.timeoutSeconds`} />
                  </Only>
                </CpLabel>
                <CpInputNumber name={`containers.${index}.${property}Probe.timeoutSeconds`} style={{ width: 200 }} />
              </div>
            </div>
            <CpErrorWarningArray
              names={[
                `containers.${index}.${property}Probe.initialDelaySeconds`,
                `containers.${index}.${property}Probe.periodSeconds`,
                `containers.${index}.${property}Probe.timeoutSeconds`,
              ]}
            />
          </CpFormItem>
          <CpFormItem>
            <div className="flex items-start gap-2">
              <div className="flex flex-col">
                <CpLabel name={`containers.${index}.${property}Probe.successThreshold`}>
                  Success Threshold
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.successThreshold`} />
                  </Only>
                </CpLabel>
                <CpInputNumber name={`containers.${index}.${property}Probe.successThreshold`} style={{ width: 200 }} />
              </div>
              <div className="flex flex-col">
                <CpLabel name={`containers.${index}.${property}Probe.failureThreshold`}>
                  Failure Threshold
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`containers.${index}.${property}Probe.failureThreshold`} />
                  </Only>
                </CpLabel>
                <CpInputNumber name={`containers.${index}.${property}Probe.failureThreshold`} style={{ width: 200 }} />
              </div>
            </div>
            <CpErrorWarningArray
              names={[
                `containers.${index}.${property}Probe.successThreshold`,
                `containers.${index}.${property}Probe.failureThreshold`,
              ]}
            />
          </CpFormItem>
        </>
      ) : null}
      {(property === "readiness" && type === "serverless" && !alreadySet && !override && servesTraffic) ||
      ((alreadySet || override) && method === "tcp") ? (
        <>
          <Alert
            showIcon
            className="mt-4 w-fit"
            type={`warning`}
            message={`Unreliable Health Check`}
            description={`Workloads configured with basic health probes may experience temporary disruptions during the rollout and scaling activities.`}
          />
        </>
      ) : null}
      {defaultProbe ? (
        <div className="text-sm mt-4" style={{ color: "var(--color-label)" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Default {label} Probe</div>
          <div className="flex gap-4 flex-wrap">
            <div>
              Method:{" "}
              {defaultProbe.exec
                ? "Command"
                : defaultProbe.grpc
                ? "GRPC"
                : defaultProbe.httpGet
                ? "HTTP"
                : defaultProbe.tcpSocket
                ? "TCP"
                : "---"}
            </div>
            {defaultProbe.tcpSocket ? <div>Port: {defaultProbe.tcpSocket.port}</div> : null}
            {defaultProbe.grpc ? <div>Port: {defaultProbe.grpc.port}</div> : null}
            {defaultProbe.httpGet ? (
              <>
                <div>Path: {defaultProbe.httpGet.path || "/"}</div>
                {defaultProbe.httpGet.port ? <div>Port: {defaultProbe.httpGet.port}</div> : null}
                <div>Scheme: {defaultProbe.httpGet.scheme}</div>
              </>
            ) : null}
            {defaultProbe.exec ? <div>Command: {defaultProbe.exec.command.join(" ")}</div> : null}
          </div>
          <div className="flex gap-4 flex-wrap mt-1">
            <div>Initial Delay: {defaultProbe.initialDelaySeconds}s</div>
            <div>Period: {defaultProbe.periodSeconds}s</div>
            <div>Timeout: {defaultProbe.timeoutSeconds}s</div>
            <div>Success: {defaultProbe.successThreshold}</div>
            <div>Failure: {defaultProbe.failureThreshold}</div>
          </div>
        </div>
      ) : null}
    </>
  );
});
